import { ArrowRightOutlined } from '@ant-design/icons';
import { Button, DatePicker, Divider, Form, Upload, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { ICON_STEP_1 } from './IconStep';
import EditorPrivacy from '../../../../../components/Shared/forms/EditorPrivacy';
import ImgCrop from 'antd-img-crop';
import { useEffect, useState } from 'react';
import GlobalService from '../../../../../services/Global.service';
import ProgressBar from './ProgressBar';
import Sticky from 'react-sticky-el';

const Step1 = (props) => {
    const { t } = useTranslation(['consent', 'translation']);
    const values = props.form.getFieldsValue();

    const onNextStep = () => {
        props.form
            .validateFields([
                'titleTh',
                'templateNameTh',
                'firstParagraphTh',
                'effectiveDate',
            ])
            .then(() => {
                props.nextStep();
            });
    };

    const [fileList, setFileList] = useState([]);
    const beforeUpload = (file) => {
        const isJpgOrPng =
            file.type === 'image/jpeg' || file.type === 'image/png';
        if (!isJpgOrPng) {
            notification['error']({
                message: t('COOKIE.YouUploadFile', { ns: 'translation' }),
            });
        }
        const isLt2M = file.size / 1024 / 1024 < 2;
        if (!isLt2M) {
            notification['error']({
                message: t('COOKIE.ImageMustSmaller', { ns: 'translation' }),
            });
        }

        return isJpgOrPng && isLt2M;
    };

    const onPreview = async (file) => {
        let src = file.url;
        if (!src) {
            src = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.readAsDataURL(file.originFileObj);

                reader.onload = () => resolve(reader.result);
            });
        }
        const image = new Image();
        image.src = src;
        const imgWindow = window.open(src);
        imgWindow?.document.write(image.outerHTML);
    };

    const getImage = async (logoId) => {
        try {
            const { data } = await GlobalService.getImage(logoId);
            if (data.resultData?.imageId) {
                setFileList([
                    {
                        uid: '-1',
                        name: 'image.png',
                        status: 'done',
                        url: `data:image/png;base64,${data.resultData?.imageId}`,
                    },
                ]);
                props?.setImage(
                    `data:image/png;base64,${data.resultData?.imageId}`
                );
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        if (values?.logoId) getImage(values?.logoId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values?.logoId]);

    const uploadProps = {
        multiple: false,
        listType: 'picture-card',
        beforeUpload,
        className: 'policy',
        accept: '.png,.jpg,.jpeg',
        fileList,
        onPreview,
        onChange(info) {
            const { status } = info.file;
            if (status === 'uploading') {
                info.file.status = 'done';
            }
            if (status === 'done' || status === 'removed') {
                setFileList(info.fileList);
            }
        },
        onSuccess(res, file) {
            props?.form?.setFieldsValue({
                logoId: res?.imageId,
            });
            getImage(res?.imageId);
        },
        onError(err) {
            console.log('onError', err);
        },
        onRemove() {
            props?.form?.setFieldsValue({
                logoId: null,
            });
            props?.setImage(null);
        },
        customRequest: async ({ file, onSuccess, onError, onProgress }) => {
            let fileList = new FormData();
            fileList.append('image', file);
            fileList.append('fileName', file.name);
            fileList.append('action', 'image-create-application');
            try {
                const onUploadProgress = ({ total, loaded }) => {
                    onProgress(
                        {
                            percent: Math.round((loaded / total) * 100).toFixed(
                                2
                            ),
                        },
                        file
                    );
                };
                const { data } = await GlobalService.uploadImage(
                    fileList,
                    onUploadProgress
                );
                if (data?.resultData?.imageId) {
                    onSuccess(data?.resultData, file);
                } else {
                    onError('error');
                }
            } catch (error) {
                onError(error);
            }
        },
    };

    const uploadButton = (
        <div>
            <svg
                width="29"
                height="29"
                viewBox="0 0 29 29"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M15.8721 7.62647H13.1221V13.1265H7.62207V15.8765H13.1221V21.3765H15.8721V15.8765H21.3721V13.1265H15.8721V7.62647ZM14.4971 0.751465C6.90707 0.751465 0.74707 6.91147 0.74707 14.5015C0.74707 22.0915 6.90707 28.2515 14.4971 28.2515C22.0871 28.2515 28.2471 22.0915 28.2471 14.5015C28.2471 6.91147 22.0871 0.751465 14.4971 0.751465ZM14.4971 25.5015C8.43332 25.5015 3.49707 20.5652 3.49707 14.5015C3.49707 8.43772 8.43332 3.50147 14.4971 3.50147C20.5608 3.50147 25.4971 8.43772 25.4971 14.5015C25.4971 20.5652 20.5608 25.5015 14.4971 25.5015Z"
                    fill="#1890FF"
                />
            </svg>

            <div className="-mt-2 text-blue-500 opacity-90 pt-1">
                {t('Add logo')}
            </div>
        </div>
    );

    return (
        <div className="bg-white p-4 rounded-lg">
            <Sticky
                boundaryElement=".block_"
                stickyClassName="bg-white p-4 shadow rounded-lg z-50"
            >
                <div className="text-right">
                    <Button type="primary" size="large" onClick={onNextStep}>
                        {t('Next Step')} <ArrowRightOutlined />
                    </Button>
                </div>
            </Sticky>
            <Divider />
            <div className="flex space-x-2 items-center">
                {ICON_STEP_1}
                <div>
                    <div className="text-base font-medium text-[#65707A]">
                        {t('Step')} 1 / 5
                    </div>
                    <div className="text-xl font-semibold">{t('Step1')}</div>
                </div>
            </div>
            <ProgressBar percent={20} />
            {/* <div className="h-[60vh] overflow-y-auto pr-2"> */}
            <Form.Item
                name="titleTh"
                label={
                    <span className="text-sm font-semibold">
                        {t('TitleDocument')} (TH)
                    </span>
                }
                rules={[
                    {
                        required: true,
                        message: t('RequiredField'),
                    },
                ]}
            >
                <EditorPrivacy placeholder={t('inputpl')} />
            </Form.Item>
            <Form.Item
                name="titleEn"
                label={
                    <span className="text-sm font-semibold">
                        {t('TitleDocument')} (EN)
                    </span>
                }
            >
                <EditorPrivacy placeholder={t('inputpl')} />
            </Form.Item>
            <Divider />
            <Form.Item
                name="templateNameTh"
                label={
                    <span className="text-sm font-semibold">
                        {t('TemplateName')} (TH)
                    </span>
                }
                rules={[
                    {
                        required: true,
                        message: t('RequiredField'),
                    },
                ]}
            >
                <EditorPrivacy placeholder={t('inputpl')} />
            </Form.Item>
            <Form.Item
                name="templateNameEn"
                label={
                    <span className="text-sm font-semibold">
                        {t('TemplateName')} (EN)
                    </span>
                }
            >
                <EditorPrivacy placeholder={t('inputpl')} />
            </Form.Item>
            <Divider />
            <Form.Item
                name="effectiveDate"
                label={
                    <span className="text-sm font-semibold">
                        {t('Consent.FieldLastUpdate', { ns: 'translation' })}
                    </span>
                }
                rules={[
                    {
                        required: true,
                        message: t('RequiredField'),
                    },
                ]}
            >
                <DatePicker
                    allowClear={false}
                    size="large"
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                />
            </Form.Item>
            <Divider />
            <Form.Item
                name="logoId"
                label={
                    <span className="text-sm font-semibold">{t('Logo')}</span>
                }
            >
                <ImgCrop
                    beforeCrop={beforeUpload}
                    showGrid
                    rotationSlider
                    aspectSlider
                    showReset
                >
                    <Upload {...uploadProps}>
                        {fileList.length < 1 && uploadButton}
                    </Upload>
                </ImgCrop>
            </Form.Item>
            <Divider />
            <Form.Item
                name="firstParagraphTh"
                label={
                    <span className="text-sm font-semibold">
                        {t('Paragraph1')} (TH)
                    </span>
                }
                rules={[
                    {
                        required: true,
                        message: t('RequiredField'),
                    },
                ]}
            >
                <EditorPrivacy placeholder={t('inputpl')} />
            </Form.Item>
            <Form.Item
                name="firstParagraphEn"
                label={
                    <span className="text-sm font-semibold">
                        {t('Paragraph1')} (EN)
                    </span>
                }
            >
                <EditorPrivacy placeholder={t('inputpl')} />
            </Form.Item>
            {/* </div> */}
        </div>
    );
};

export default Step1;
